import { useState, useEffect, useRef } from 'react';
import { FormControlLabel, Radio, RadioGroup, Box, CircularProgress } from '@mui/material';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ChartData } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import pannelState from 'state/rightPannelState';
import { useRecoilValue } from 'recoil';
import { GridArea } from 'components/common/GridArea';
import { AgGridReact } from 'ag-grid-react';
import { CropSchema } from 'constant/CropSchema';
import { EmptyContent, ProgressBackdrop } from 'packages/ui/src/components';
import RadioIcon from 'packages/ui/src/assets/images/icon-radio.svg';
import CheckedRadioIcon from 'packages/ui/src/assets/images/icon-radio-checked.svg';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

type Props = {
  resourceText?: {
    imageNm: string;
    portDate: string;
  };
  chartData?: ChartData<'doughnut', any, any>;
  gridData?: Array<any>;
};

const Styles = {
  margin: '0 16px',
  '&>.MuiButtonBase-root': {
    padding: '0',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&>.css-hyxlzm': {
      width: '18px',
      height: '18px',
      marginRight: '6px',
      backgroundImage: `url(${RadioIcon})`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: 'contain',
      '&>svg': {
        display: 'none',
      },
    },
    '&.Mui-checked>.css-hyxlzm': {
      backgroundImage: `url(${CheckedRadioIcon})`,
    },
    '&>.MuiTouchRipple-root': {
      display: 'none',
    },
  },
  '&>.MuiTypography-root': {
    display: 'inline-block',
    verticalAlign: 'top',
    color: '#383F43',
    fontSize: '0.875rem',
    lineHeight: '1.286em',
    letterSpacing: '-0.28px',
    fontFamily: 'NotoSansKR',
  },
};

const AiChartWithGrid = ({ chartData, gridData }: Props) => {
  const pannel = useRecoilValue(pannelState);

  return (
    <>
      {chartData.datasets.length > 0 ? (
        <>
          <StatisticsChart chartData={chartData} />
          <StatisticsGrid gridData={gridData} />
        </>
      ) : (
        <EmptyContent text="분석 결과가 존재하지 않습니다." />
      )}
      <ProgressBackdrop isLoading={pannel.status.isLoading} />
    </>
  );
};

const StatisticsChart = ({ chartData }: { chartData?: ChartData<'doughnut', any, any> }) => {
  const [feature, setFeature] = useState<string>('area');
  const [chart, setChart] = useState(chartData);

  useEffect(() => {
    const data = chartData.datasets[0].data;
    const _data = data.map((i) => i[feature]);
    setChart((prev) => ({
      ...prev,
      datasets: [{ data: _data, backgroundColor: chartData.datasets[0].backgroundColor }],
      labels: chartData.labels,
    }));
  }, [chartData]);

  useEffect(() => {
    const data = chartData.datasets[0].data;
    if (data.length < 1) {
      setChart((prev) => ({ ...prev, datasets: [{ ...prev.datasets[0], data: [] }] }));
      return;
    }
    const _data = data.map((i) => i[feature]);
    setChart((prev) => ({ ...prev, datasets: [{ ...prev.datasets[0], data: _data }] }));
  }, [feature]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFeature((prev) => (event.target as HTMLInputElement).value);
  };

  return (
    <Box className="micChartWrap">
      <div className="micChartArea">
        <div className="micChartTerms">
          {/* <RadioGroup
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
            defaultValue="area"
            value={feature}
            onChange={handleChange}
            row
          >
          </RadioGroup> */}
          <Doughnut
            data={chart}
            options={{
              plugins: {
                datalabels: {
                  color: 'white',
                  // display: false,
                  textAlign: 'center',
                  formatter(value, context) {
                    // data 에 넣은 데이타 순번. 물론 0 부터 시작
                    const idx = context.dataIndex; // 여기선 첫번째 데이타엔 단위를 '원' 으로, 그 다음 데이타엔 'P' 를 사용

                    // addComma() 는 여기서 기술하지 않았지만, 천단위 세팅. ChartJS 의 data 엔 숫자만 입력
                    return [`${context.chart.data.labels[idx]}`];
                  },
                },
                legend: {
                  position: 'bottom',
                  labels: {
                    boxWidth: 12,
                    boxHeight: 12,
                    padding: 16,
                    usePointStyle: true,
                    pointStyle: 'circle',
                    color: '#1E1D1F',
                    font: {
                      family: "'NotoSansKR', sans-serif",
                      size: 13,
                    },
                  },
                },
              },
            }}
          />
        </div>
      </div>
    </Box>
  );
};

const StatisticsGrid = ({ gridData }: { gridData?: Array<any> }) => {
  const gridRef = useRef<AgGridReact>(null);
  const [grid, setGrid] = useState(gridData);
  const onFirstDataRendered = (params) => {
    params.api.sizeColumnsToFit();
  };

  useEffect(() => {
    setGrid(gridData);
  }, [gridData]);

  return (
    <Box className="cListTableWrap">
      <Box className="cListTableArea">
        <GridArea
          gridRef={gridRef}
          rowData={grid}
          columnDefs={CropSchema.statistics.columnDefs}
          isPaging={false}
          onFirstDataRendered={onFirstDataRendered}
          defaultColDef={{
            sortable: true,
            resizable: true,
          }}
          domLayout="autoHeight"
        />
      </Box>
    </Box>
  );
};

export default AiChartWithGrid;
